// KINTSUGI : le réseau de fêlures d'un bol, généré une fois par morceau dans
// un Web Worker (R17), livré en deux textures de données.
//
// generateCracks() est AUTONOME (aucune référence extérieure, rien après
// ES2017) : le worker est construit à partir de sa source, et la même
// fonction tourne sous Node dans les tests.
//
// LE RÉSEAU, dans le disque unité du fond du bol (rayon 1 = le bord de la
// glaçure) :
//   1. 12 à 20 germes, tirés hors du centre (la pochette DOM repose au fond
//      du bol et en cache le milieu), écartés d'au moins 0,22 ;
//   2. leur diagramme de Voronoï, exact : chaque triplet de germes donne un
//      sommet (centre du cercle circonscrit) s'il n'existe pas de germe plus
//      proche ; une arête relie les deux sommets que partagent deux germes.
//      Des germes fantômes sur un grand cercle bornent les cellules du bord.
//      Les arêtes sont coupées au bord de la glaçure ;
//   3. on ne garde qu'une partie des arêtes, SANS JAMAIS déconnecter le
//      réseau (un arbre couvrant tiré au hasard, puis 35 % des autres) :
//      sinon l'or apparaîtrait d'un coup dans un morceau isolé. Certaines
//      arêtes écartées restent à moitié : des fêlures qui s'arrêtent ;
//   4. chaque arête est DÉFORMÉE : subdivisée et déplacée perpendiculairement
//      par un bruit lisse (les extrémités restent fixes) ;
//   5. des fêlures secondaires fines partent des principales ;
//   6. L'ORDRE : l'or part d'un point du bord ; Dijkstra donne à chaque nœud
//      sa distance de chemin, et chaque point d'une arête la plus courte des
//      deux arrivées (par l'un ou l'autre bout). Une secondaire hérite de
//      l'arrivée de sa racine. L'or avance donc de proche en proche.
//
// LES TEXTURES (N x N, couvrant le carré [-1, 1]²) :
//   dist  RGBA8, filtrée linéairement : R = distance à la fêlure la plus
//         proche, en fraction de la bande (BAND texels) ; G = 255 pour une
//         secondaire. R2 : on stocke une distance, le seuillage se fait en
//         pleine résolution dans le shader.
//   arr   RGBA8, au plus proche : R, G = l'arrivée sur 16 bits (fraction de
//         maxArr), B = la position le long de la fêlure (pour l'épaisseur
//         irrégulière de l'or), 8 bits cycliques.

export const CRACK_N = 1024;
export const CRACK_BAND = 12;          // texels

export function generateCracks(seed, N) {
  var BAND = 12;
  // --- le hasard, reproductible ---
  var s = (seed >>> 0) || 1;
  function rnd() { s = (s + 0x6D2B79F5) >>> 0; var t = s; t = Math.imul(t ^ (t >>> 15), t | 1); t ^= t + Math.imul(t ^ (t >>> 7), t | 61); return ((t ^ (t >>> 14)) >>> 0) / 4294967296; }
  var i, j, k;
  function voronoiVertices(sites) {
    var S = sites.length, out = [], i, j, k, m;
    for (i = 0; i < S; i++) for (j = i + 1; j < S; j++) for (k = j + 1; k < S; k++) {
      var ax = sites[i][0], ay = sites[i][1], bx = sites[j][0], by = sites[j][1], cx = sites[k][0], cy = sites[k][1];
      var d = 2 * (ax * (by - cy) + bx * (cy - ay) + cx * (ay - by));
      if (d < 1e-12 && d > -1e-12) continue;
      var a2 = ax * ax + ay * ay, b2 = bx * bx + by * by, c2 = cx * cx + cy * cy;
      var ux = (a2 * (by - cy) + b2 * (cy - ay) + c2 * (ay - by)) / d;
      var uy = (a2 * (cx - bx) + b2 * (ax - cx) + c2 * (bx - ax)) / d;
      if (ux * ux + uy * uy > 9) continue;
      var rc2 = (ux - ax) * (ux - ax) + (uy - ay) * (uy - ay) - 1e-9;
      var valid = true;
      for (m = 0; m < S; m++) {
        if (m === i || m === j || m === k) continue;
        var ex = ux - sites[m][0], ey = uy - sites[m][1];
        if (ex * ex + ey * ey < rc2) { valid = false; break; }
      }
      if (valid) out.push({ x: ux, y: uy, s: [i, j, k] });
    }
    return out;
  }
  function rasterise(st, N, BAND, best2, arrT, clsT) {
    var sp = st.pts, sa = st.arr, cl = st.cls, toTex = N / 2, bandU = BAND / toTex;
    for (var j = 0; j + 1 < sp.length / 2; j++) {
      var x0 = sp[2 * j], y0 = sp[2 * j + 1], x1 = sp[2 * j + 2], y1 = sp[2 * j + 3];
      var a0s = sa[j], a1s = sa[j + 1];
      var minX = Math.max(0, Math.floor((Math.min(x0, x1) - bandU + 1) * toTex)), maxX = Math.min(N - 1, Math.ceil((Math.max(x0, x1) + bandU + 1) * toTex));
      var minY = Math.max(0, Math.floor((Math.min(y0, y1) - bandU + 1) * toTex)), maxY = Math.min(N - 1, Math.ceil((Math.max(y0, y1) + bandU + 1) * toTex));
      var sx = x1 - x0, sy = y1 - y0, sl2 = sx * sx + sy * sy || 1e-12;
      for (var ty = minY; ty <= maxY; ty++) {
        var py = (ty + 0.5) / toTex - 1, row = ty * N;
        for (var tx = minX; tx <= maxX; tx++) {
          var px = (tx + 0.5) / toTex - 1;
          var tt = ((px - x0) * sx + (py - y0) * sy) / sl2;
          tt = tt < 0 ? 0 : (tt > 1 ? 1 : tt);
          var qx = x0 + sx * tt - px, qy = y0 + sy * tt - py;
          var dd = qx * qx + qy * qy, o = row + tx;
          if (dd < best2[o]) { best2[o] = dd; arrT[o] = a0s + (a1s - a0s) * tt; clsT[o] = cl; }
        }
      }
    }
  }
  function pack(N, BAND, maxArr, best2, arrT, clsT, dist8, arr8) {
    var NN = N * N, toTex = N / 2, k255 = 255 / BAND, inv = 65535 / maxArr;
    for (var i = 0; i < NN; i++) {
      var o4 = 4 * i;
      var dT = Math.sqrt(best2[i]) * toTex * k255;
      dist8[o4] = dT > 255 ? 255 : (dT + 0.5) | 0;
      dist8[o4 + 1] = clsT[i] ? 255 : 0;
      dist8[o4 + 3] = 255;
      var a = arrT[i];
      var qa = a >= maxArr ? 65535 : (a * inv + 0.5) | 0;
      arr8[o4] = qa >> 8; arr8[o4 + 1] = qa & 255;
      var f = a * 37; arr8[o4 + 2] = ((f - Math.floor(f)) * 255) | 0;
      arr8[o4 + 3] = 255;
    }
  }
  // 1. les germes
  var nSites = 12 + Math.floor(rnd() * 9);
  var sites = [];
  var guard = 0;
  while (sites.length < nSites && guard++ < 5000) {
    var r = 0.32 + 0.62 * Math.sqrt(rnd()), a = rnd() * Math.PI * 2;
    var p = [r * Math.cos(a), r * Math.sin(a)];
    var ok = true;
    for (i = 0; i < sites.length; i++) if (Math.hypot(p[0] - sites[i][0], p[1] - sites[i][1]) < 0.22) { ok = false; break; }
    if (ok) sites.push(p);
  }
  // quelques germes vers le centre aussi (sous la pochette : le réseau y passe)
  for (i = 0; i < 3; i++) { var a2 = rnd() * Math.PI * 2, r2 = 0.12 * rnd(); sites.push([r2 * Math.cos(a2), r2 * Math.sin(a2)]); }
  var nReal = sites.length;
  for (i = 0; i < 14; i++) { var ag = (i + 0.5) / 14 * Math.PI * 2; sites.push([2.6 * Math.cos(ag), 2.6 * Math.sin(ag)]); }
  var S = sites.length;

  // 2. Voronoï exact par triplets
  var verts = voronoiVertices(sites);
  // the edges: two vertices sharing a pair of sites
  var RIM = 1.0;
  var rawEdges = [];
  for (i = 0; i < verts.length; i++) for (j = i + 1; j < verts.length; j++) {
    var sh = 0, A = verts[i].s, B = verts[j].s;
    for (var q = 0; q < 3; q++) if (B.indexOf(A[q]) >= 0) sh++;
    if (sh >= 2 && Math.hypot(verts[i].x - verts[j].x, verts[i].y - verts[j].y) > 1e-6) rawEdges.push([i, j]);
  }
  // the graph: nodes = vertices inside the rim + rim crossings
  var nodes = [], nodeOf = {};
  function nodeAt(x, y, rim) { nodes.push({ x: x, y: y, rim: rim }); return nodes.length - 1; }
  function vnode(vi) { if (nodeOf[vi] === undefined) nodeOf[vi] = nodeAt(verts[vi].x, verts[vi].y, false); return nodeOf[vi]; }
  var edges = [];          // { a, b (nodes) }
  for (i = 0; i < rawEdges.length; i++) {
    var va = verts[rawEdges[i][0]], vb = verts[rawEdges[i][1]];
    var ia = Math.hypot(va.x, va.y) <= RIM, ib = Math.hypot(vb.x, vb.y) <= RIM;
    if (!ia && !ib) continue;
    if (ia && ib) { edges.push({ a: vnode(rawEdges[i][0]), b: vnode(rawEdges[i][1]) }); continue; }
    // clip to the rim: the inside end, and the crossing
    var pin = ia ? va : vb, pout = ia ? vb : va;
    var dx = pout.x - pin.x, dy = pout.y - pin.y;
    var aa = dx * dx + dy * dy, bb = 2 * (pin.x * dx + pin.y * dy), cc = pin.x * pin.x + pin.y * pin.y - RIM * RIM;
    var t = (-bb + Math.sqrt(Math.max(0, bb * bb - 4 * aa * cc))) / (2 * aa);
    var rimN = nodeAt(pin.x + dx * t * 0.995, pin.y + dy * t * 0.995, true);
    edges.push({ a: vnode(ia ? rawEdges[i][0] : rawEdges[i][1]), b: rimN });
  }

  // 3. a connected subset: a random spanning tree, then 35 % of the rest
  var parent = nodes.map(function (_, n) { return n; });
  function find(x) { while (parent[x] !== x) { parent[x] = parent[parent[x]]; x = parent[x]; } return x; }
  var order = edges.map(function (_, n) { return [rnd(), n]; }).sort(function (x, y) { return x[0] - y[0]; });
  var kept = [], rest = [];
  for (i = 0; i < order.length; i++) {
    var e = edges[order[i][1]], ra = find(e.a), rb = find(e.b);
    if (ra !== rb) { parent[ra] = rb; kept.push(e); } else rest.push(e);
  }
  var partials = [];
  for (i = 0; i < rest.length; i++) {
    var u = rnd();
    if (u < 0.35) kept.push(rest[i]);
    else if (u < 0.65) {
      var fromA = rnd() < 0.5;
      partials.push({ a: fromA ? rest[i].a : rest[i].b, b: fromA ? rest[i].b : rest[i].a, f: 0.3 + 0.4 * rnd(), e: rest[i] });
    }
  }
  // keep only the component that the gold will enter (the tree may still be
  // a forest when edges were clipped away): the largest one
  var comp = {};
  for (i = 0; i < kept.length; i++) { var root = find(kept[i].a); comp[root] = (comp[root] || 0) + 1; }
  var best = null, bestN = -1;
  for (var key in comp) if (comp[key] > bestN) { bestN = comp[key]; best = +key; }
  kept = kept.filter(function (e) { return find(e.a) === best; });
  partials = partials.filter(function (p2) { return find(p2.e.a) === best; });

  // 4. deformed polylines
  function noise1(x, ph) { return Math.sin(x * 1.7 + ph) * 0.55 + Math.sin(x * 4.3 + ph * 2.1) * 0.3 + Math.sin(x * 11.1 + ph * 3.7) * 0.15; }
  function polyline(x0, y0, x1, y1, amp) {
    var L = Math.hypot(x1 - x0, y1 - y0), n = Math.max(4, Math.ceil(L / 0.01));
    var nx = -(y1 - y0) / L, ny = (x1 - x0) / L, ph = rnd() * 100;
    var pts = [];
    for (var m = 0; m <= n; m++) {
      var t = m / n, env = Math.sin(Math.PI * t);
      var off = amp * env * noise1(t * L * 6, ph) + 0.004 * env * Math.sin(t * L * 90 + ph);
      var px = x0 + (x1 - x0) * t + nx * off, py = y0 + (y1 - y0) * t + ny * off;
      var rr = Math.hypot(px, py);
      if (rr > 0.995) { px *= 0.995 / rr; py *= 0.995 / rr; }
      pts.push(px, py);
    }
    return pts;
  }
  function plen(p) { var L = 0; for (var m = 2; m < p.length; m += 2) L += Math.hypot(p[m] - p[m - 2], p[m + 1] - p[m - 1]); return L; }
  for (i = 0; i < kept.length; i++) {
    var ea = nodes[kept[i].a], eb = nodes[kept[i].b];
    kept[i].pts = polyline(ea.x, ea.y, eb.x, eb.y, 0.035);
    kept[i].len = plen(kept[i].pts);
  }

  // 6. the order: Dijkstra from a rim node (or the node nearest the rim)
  var adj = nodes.map(function () { return []; });
  for (i = 0; i < kept.length; i++) { adj[kept[i].a].push([kept[i].b, kept[i].len]); adj[kept[i].b].push([kept[i].a, kept[i].len]); }
  var startNode = -1, bestR = -1;
  var rimNodes = [];
  for (i = 0; i < nodes.length; i++) if (adj[i].length && nodes[i].rim) rimNodes.push(i);
  if (rimNodes.length) startNode = rimNodes[Math.floor(rnd() * rimNodes.length)];
  else for (i = 0; i < nodes.length; i++) if (adj[i].length) { var rr2 = Math.hypot(nodes[i].x, nodes[i].y); if (rr2 > bestR) { bestR = rr2; startNode = i; } }
  var dist = nodes.map(function () { return Infinity; });
  var done = nodes.map(function () { return false; });
  dist[startNode] = 0;
  for (;;) {
    var bi = -1, bd = Infinity;
    for (i = 0; i < nodes.length; i++) if (!done[i] && dist[i] < bd) { bd = dist[i]; bi = i; }
    if (bi < 0) break;
    done[bi] = true;
    for (j = 0; j < adj[bi].length; j++) { var to = adj[bi][j][0], w = adj[bi][j][1]; if (dist[bi] + w < dist[to]) dist[to] = dist[bi] + w; }
  }

  // the strokes to rasterise: points with their arrival
  var strokes = [];     // { pts, arr (per point), cls }
  for (i = 0; i < kept.length; i++) {
    var E = kept[i], da = dist[E.a], db = dist[E.b];
    if (!isFinite(da) || !isFinite(db)) continue;
    var arr = [], acc = 0;
    for (j = 0; j < E.pts.length / 2; j++) {
      if (j > 0) acc += Math.hypot(E.pts[2 * j] - E.pts[2 * j - 2], E.pts[2 * j + 1] - E.pts[2 * j - 1]);
      arr.push(Math.min(da + acc, db + (E.len - acc)));
    }
    strokes.push({ pts: E.pts, arr: arr, cls: 0 });
  }
  // the half edges: from their node, part of the way
  for (i = 0; i < partials.length; i++) {
    var P = partials[i], n0 = nodes[P.a], n1 = nodes[P.b];
    if (!isFinite(dist[P.a])) continue;
    var ex = n0.x + (n1.x - n0.x) * P.f, ey = n0.y + (n1.y - n0.y) * P.f;
    var pp = polyline(n0.x, n0.y, ex, ey, 0.025);
    var ar = [], ac = 0;
    for (j = 0; j < pp.length / 2; j++) { if (j > 0) ac += Math.hypot(pp[2 * j] - pp[2 * j - 2], pp[2 * j + 1] - pp[2 * j - 1]); ar.push(dist[P.a] + ac); }
    strokes.push({ pts: pp, arr: ar, cls: 0 });
  }
  // 5. secondary cracks from the main ones
  var mainCount = strokes.length;
  var nSec = 10 + Math.floor(rnd() * 9);
  for (i = 0; i < nSec && mainCount; i++) {
    var st = strokes[Math.floor(rnd() * mainCount)];
    var np = st.pts.length / 2;
    if (np < 6) continue;
    var at = 2 + Math.floor(rnd() * (np - 4));
    var x = st.pts[2 * at], y = st.pts[2 * at + 1];
    var tx = st.pts[2 * at + 2] - st.pts[2 * at - 2], ty = st.pts[2 * at + 3] - st.pts[2 * at - 1], tl = Math.hypot(tx, ty) || 1;
    var side = rnd() < 0.5 ? -1 : 1;
    var dir = Math.atan2(ty, tx) + side * (Math.PI / 2 + (rnd() - 0.5) * 1.2);
    var steps = 6 + Math.floor(rnd() * 10), turn = (rnd() - 0.5) * 0.25;
    var bp = [x, y], ba = [st.arr[at]], a0 = st.arr[at];
    for (j = 1; j <= steps; j++) {
      dir += turn + (rnd() - 0.5) * 0.35;
      x += Math.cos(dir) * 0.011; y += Math.sin(dir) * 0.011;
      if (Math.hypot(x, y) > 0.99) break;
      bp.push(x, y); a0 += 0.011; ba.push(a0);
    }
    if (bp.length >= 6) strokes.push({ pts: bp, arr: ba, cls: 1 });
  }

  var maxArr = 0;
  for (i = 0; i < strokes.length; i++) for (j = 0; j < strokes[i].arr.length; j++) if (strokes[i].arr[j] > maxArr) maxArr = strokes[i].arr[j];
  maxArr = Math.max(maxArr, 1e-3);

  // rasterise, then pack (inner functions: V8 optimises them one by one)
  var NN = N * N;
  var best2 = new Float32Array(NN); best2.fill(1e9);
  var arrT = new Float32Array(NN), clsT = new Uint8Array(NN);
  for (i = 0; i < strokes.length; i++) rasterise(strokes[i], N, BAND, best2, arrT, clsT);
  var dist8 = new Uint8Array(NN * 4), arr8 = new Uint8Array(NN * 4);
  pack(N, BAND, maxArr, best2, arrT, clsT, dist8, arr8);
  return { n: N, band: BAND, dist: dist8, arr: arr8, maxArr: maxArr, start: [nodes[startNode].x, nodes[startNode].y],
    stats: { sites: nReal, verts: verts.length, edges: edges.length, kept: kept.length, partials: partials.length, strokes: strokes.length, secondary: strokes.length - mainCount } };
}

export function cracksWorkerSource() {
  return 'var generateCracks = ' + generateCracks.toString() + ';\n' +
    'try { generateCracks(7, 256); generateCracks(9, 256); } catch (e) {}\n' +
    'self.onmessage = function (ev) {\n' +
    '  var m = ev.data;\n' +
    '  try {\n' +
    '    var t0 = performance.now();\n' +
    '    var res = generateCracks(m.seed, m.n);\n' +
    '    res.ms = performance.now() - t0;\n' +
    '    self.postMessage({ id: m.id, res: res }, [res.dist.buffer, res.arr.buffer]);\n' +
    '  } catch (e) { self.postMessage({ id: m.id, error: String(e && e.message || e) }); }\n' +
    '};\n';
}

// the seed: a 32-bit hash of the title (and artist)
export function seedOf(text) {
  var h = 2166136261 >>> 0;
  var s = String(text || '');
  for (var i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 16777619) >>> 0; }
  return h || 1;
}
